/**
 * Interface for chat filter parameters
 * Allows for type-safe query keys when filtering chat messages
 */
export interface ChatFilters {
  topicId?: string;
  userId?: string; 
  role?: 'user' | 'assistant' | 'system';
  limit?: number;
  sortDirection?: 'asc' | 'desc';
}

/**
 * Query key factory for chat messages
 * These keys are used to identify and organize React Query cache entries
 * related to chat messages. Following TanStack Query best practices for structured keys.
 * 
 * Query keys are organized hierarchically to allow for selective invalidation:
 * - chatKeys.all: Invalidates all chat-related queries
 * - chatKeys.lists(): Invalidates all chat list queries
 * - chatKeys.list(filters): Invalidates specific filtered lists
 * - chatKeys.byTopic(topicId): Invalidates all chat messages for a specific topic
 * - chatKeys.details(): Invalidates all individual chat message queries
 * - chatKeys.detail(id): Invalidates a specific chat message
 */
export const chatKeys = {
  /**
   * Base key for all chat-related queries
   * Invalidating this key will invalidate all chat queries
   */
  all: ['chats'] as const,
  
  /**
   * Keys for all chat message lists
   * Invalidating this key will invalidate all list queries but not detail queries
   * 
   * @returns Tuple of ['chats', 'list']
   */
  lists: () => [...chatKeys.all, 'list'] as const,
  
  /**
   * Key for filtered chat lists
   * Used for storing and retrieving specific filtered views of chat messages
   * 
   * @param filters - Object containing filter criteria
   * @returns Tuple of ['chats', 'list', {...filters}]
   * 
   * @example
   * // Get chat messages for a specific topic
   * chatKeys.list({ topicId: 'topic-123' })
   */
  list: (filters: ChatFilters = {}) => [...chatKeys.lists(), filters] as const,
  
  /**
   * Keys for all individual chat message details
   * Invalidating this will invalidate all individual chat message queries
   * 
   * @returns Tuple of ['chats', 'detail']
   */
  details: () => [...chatKeys.all, 'detail'] as const,
  
  /**
   * Key for a specific chat message by ID
   * Used for storing and retrieving data about a specific chat message
   * 
   * @param id - Chat message ID
   * @returns Tuple of ['chats', 'detail', id]
   */
  detail: (id: string) => [...chatKeys.details(), id] as const,
  
  /**
   * Key for all chat messages in a specific topic
   * Useful for invalidating all chat messages when a topic changes
   * 
   * @param topicId - Topic ID
   * @returns Tuple of ['chats', 'by-topic', topicId]
   */
  byTopic: (topicId: string) => [...chatKeys.all, 'by-topic', topicId] as const,
};